import { createComponent } from 'reakit-system/createComponent';
import { createHook } from 'reakit-system/createHook';
import { useBox } from '../Box/Box.js';
import { _ as _objectSpread2, a as _objectWithoutPropertiesLoose } from '../_rollupPluginBabelHelpers-ce04ac6e.js';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useForkRef } from 'reakit-utils/useForkRef';
import { isButton } from 'reakit-utils/isButton';
import { isFocusable } from 'reakit-utils/tabbable';
import { hasFocusWithin } from 'reakit-utils/hasFocusWithin';
import { warning } from 'reakit-warning';

function isNativeTabbable(element) {
  return element.tagName === "BUTTON" || element.tagName === "INPUT" || element.tagName === "SELECT" || element.tagName === "TEXTAREA" || element.tagName === "A" || element.tagName === "AUDIO" || element.tagName === "VIDEO";
} // https://twitter.com/diegohaz/status/1176998102139572225


function isUserAgent(string) {
  if (typeof window === "undefined") return false;
  return window.navigator.userAgent.indexOf(string) !== -1;
}

var isSafariOrFirefoxOnMac = isUserAgent("Mac") && (isUserAgent("Safari") || isUserAgent("Firefox"));
var useTabbable = createHook({
  name: "Tabbable",
  compose: useBox,
  keys: ["disabled", "focusable"],
  useOptions: function useOptions(options, _ref) {
    var disabled = _ref.disabled;
    return _objectSpread2({
      disabled: disabled
    }, options);
  },
  useProps: function useProps(options, _ref2) {
    var htmlRef = _ref2.ref,
        htmlTabIndex = _ref2.tabIndex,
        htmlOnClick = _ref2.onClick,
        htmlOnMouseDown = _ref2.onMouseDown,
        htmlStyle = _ref2.style,
        htmlProps = _objectWithoutPropertiesLoose(_ref2, ["ref", "tabIndex", "onClick", "onMouseDown", "style"]);

    var ref = useRef(null);
    var trulyDisabled = options.disabled && !options.focusable;

    var _React$useState = useState(true),
        nativeTabbable = _React$useState[0],
        setNativeTabbable = _React$useState[1];

    var tabIndex = nativeTabbable ? htmlTabIndex : htmlTabIndex || 0;
    var style = trulyDisabled ? _objectSpread2({
      pointerEvents: "none"
    }, htmlStyle) : htmlStyle;
    useEffect(function () {
      var tabbable = ref.current;

      if (!tabbable) {
        process.env.NODE_ENV !== "production" ? warning(true, "Can't determine if the element is a native tabbable element because `ref` wasn't passed to the component.", "See https://reakit.io/docs/tabbable") : void 0;
        return;
      }

      if (!isNativeTabbable(tabbable)) {
        setNativeTabbable(false);
      }
    }, []);
    var onClick = useCallback(function (event) {
      if (options.disabled) {
        event.stopPropagation();
        event.preventDefault();
      } else if (htmlOnClick) {
        htmlOnClick(event);
      }
    }, [options.disabled, htmlOnClick]);
    var onMouseDown = useCallback(function (event) {
      if (options.disabled) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }

      if (htmlOnMouseDown) {
        htmlOnMouseDown(event);
      }

      if (event.defaultPrevented) {
        return;
      }

      var self = event.currentTarget;
      var target = event.target;

      if (isSafariOrFirefoxOnMac && isButton(self) && self.contains(target)) {
        event.preventDefault();
        var isFocusControl = isFocusable(target) || target instanceof HTMLLabelElement;

        if (!hasFocusWithin(self) || self === target || !isFocusControl) {
          self.focus();
        }
      }
    }, [options.disabled, htmlOnMouseDown]);
    return _objectSpread2({
      ref: useForkRef(ref, htmlRef),
      onClick: onClick,
      onMouseDown: onMouseDown,
      style: style
    }, trulyDisabled && nativeTabbable ? {
      disabled: true
    } : {}, {}, options.disabled ? {
      "aria-disabled": true
    } : {}, {}, trulyDisabled ? {} : {
      tabIndex: tabIndex
    }, {}, htmlProps);
  }
});
var Tabbable = createComponent({
  as: "div",
  useHook: useTabbable
});

export { Tabbable, useTabbable };
